// Grouped, sortable task table (11a §5). Groups collapse; headers sort.
import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, ArrowUp, ArrowDown } from "lucide-react";
import TaskRowV2, { TASK_GRID } from "./TaskRowV2";
import { sortTasks, dueInfo } from "../_shared";

const COLS = [["", null], ["Status", "status"], ["Task", "title"], ["Link", null], ["Owner", "assignee"], ["Priority", "priority"], ["Frequency", "freq"], ["Due", "due"], ["Progress", "progress"], ["Last activity", "last"], ["", null]];
const DUE_ORDER = ["Overdue", "Today", "This week", "Later", "No date", "Done"];

function dueGroup(t) {
  if (["done", "cancelled"].includes(t.status)) return "Done";
  const d = dueInfo(t);
  if (!t.due_date) return "No date";
  if (d.tone === "red") return "Overdue";
  if (d.days === 0) return "Today";
  const end = 7 - (new Date().getDay() || 7);
  return d.days <= end ? "This week" : "Later";
}

export function groupTasks(tasks, by, { teamMap, userMap, statuses }) {
  if (by === "none") return [["All", tasks]];
  const key = (t) => ({
    due: dueGroup(t),
    team: teamMap[t.team_id]?.name || "Personal",
    status: statuses.find((s) => s.key === t.status)?.label || t.status,
    priority: t.priority, type: t.type,
    owner: t.assignee_id ? (userMap[t.assignee_id]?.name || "Someone") : (t.team_id ? "Pool" : "Unassigned"),
  }[by]);
  const m = new Map();
  tasks.forEach((t) => { const k = key(t) || "—"; if (!m.has(k)) m.set(k, []); m.get(k).push(t); });
  const entries = [...m.entries()];
  if (by === "due") entries.sort((a, b) => DUE_ORDER.indexOf(a[0]) - DUE_ORDER.indexOf(b[0]));
  return entries;
}

export default function TaskTable({ tasks, groupBy = "due", statuses, userMap, teamMap, onOpen, onChanged, selectable, selected, onToggleSelect, onToggleAll, emptyText }) {
  const [sort, setSort] = useState({ key: null, dir: null });
  const [collapsed, setCollapsed] = useState({});
  const groups = useMemo(() => groupTasks(sortTasks(tasks, sort.key, sort.dir), groupBy, { teamMap, userMap, statuses }), [tasks, sort, groupBy, teamMap, userMap, statuses]);
  const clickSort = (k) => k && setSort((s) => s.key !== k ? { key: k, dir: "asc" } : s.dir === "asc" ? { key: k, dir: "desc" } : { key: null, dir: null });
  const all = tasks.length > 0 && tasks.every((t) => selected?.has(t.id));
  return (
    <div className="ws-card p-0 overflow-x-auto" data-testid="task-table">
      <div className="min-w-[1100px]">
        <div className="ws-v2-head" style={{ gridTemplateColumns: TASK_GRID }}>
          {COLS.map(([label, k], i) => (
            <button key={i} onClick={() => clickSort(k)} className={`text-left inline-flex items-center gap-1 ${k ? "hover:text-white" : "cursor-default"}`} data-testid={k ? `task-sort-${k}` : undefined}>
              {i === 0 && selectable ? <input type="checkbox" checked={all} onChange={(e) => onToggleAll?.(e.target.checked)} className="accent-teal-500 w-4 h-4" data-testid="task-select-all" /> : label}
              {sort.key === k && k && (sort.dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
            </button>
          ))}
        </div>
        {tasks.length === 0 && <div className="text-center text-slate-500 py-10 text-[13px]" data-testid="task-table-empty">{emptyText || "Nothing here."}</div>}
        {groups.map(([g, rows]) => (
          <div key={g} data-testid={`task-group-${g.toLowerCase().replace(/\s+/g, "-")}`}>
            {groupBy !== "none" && (
              <button onClick={() => setCollapsed((c) => ({ ...c, [g]: !c[g] }))} className={`ws-v2-group ${g === "Overdue" ? "text-rose-300" : "text-slate-400"}`}>
                {collapsed[g] ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}{g} <span className="text-slate-500">{rows.length}</span>
              </button>
            )}
            {!collapsed[g] && rows.map((t) => (
              <TaskRowV2 key={t.id} task={t} statuses={statuses} userMap={userMap} teamMap={teamMap} onOpen={onOpen} onChanged={onChanged}
                selectable={selectable} selected={selected?.has(t.id)} onToggleSelect={onToggleSelect} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

